/**
 * Valuation Lines Series Component
 *
 * Renders up to 7 horizontal lines for Valuation Lines:
 * - average: Mean/median/harmonic average
 * - ±1σ, ±2σ, ±3σ: Standard deviation bands
 *
 * All lines span the full chart width with constant values (recalculate on zoom/pan).
 * Lines can be toggled on/off via display properties.
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import { hexToRGBA, getStrokeDasharray, isDefined } from "../utils";
import { formatNumber } from "../../../utils/numberFormatting.js";

class ValuationLinesSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.calculateValuationLines = this.calculateValuationLines.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  calculateValuationLines(plotData, sourcePath, averageType) {
    // Extract values from visible plotData
    const values = [];
    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const value = d[sourcePath];
      if (isDefined(value) && isFinite(value)) {
        values.push(value);
      }
    }

    if (values.length < 2) return null;

    // Calculate average
    let average;
    if (averageType === "median") {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      average =
        sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
    } else if (averageType === "harmonic") {
      const nonZero = values.filter((v) => v !== 0);
      if (nonZero.length === 0) return null;
      const sumOfReciprocals = nonZero.reduce((acc, val) => acc + 1 / val, 0);
      average = nonZero.length / sumOfReciprocals;
    } else {
      // mean (arithmetic)
      const sum = values.reduce((acc, val) => acc + val, 0);
      average = sum / values.length;
    }

    // Calculate standard deviation
    const squaredDiffs = values.map((v) => Math.pow(v - average, 2));
    const avgSquaredDiff =
      squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
    const stdDev = Math.sqrt(avgSquaredDiff);

    return {
      average: average,
      plusOneStdDev: average + stdDev,
      minusOneStdDev: average - stdDev,
      plusTwoStdDev: average + 2 * stdDev,
      minusTwoStdDev: average - 2 * stdDev,
      plusThreeStdDev: average + 3 * stdDev,
      minusThreeStdDev: average - 3 * stdDev,
    };
  }

  drawOnCanvas(ctx, moreProps) {
    const { sourcePath, averageType } = this.props;
    const {
      displayAverage,
      displayOneStdDev,
      displayTwoStdDev,
      displayThreeStdDev,
      strokeAverage,
      strokePlusOne,
      strokeMinusOne,
      strokePlusTwo,
      strokeMinusTwo,
      strokePlusThree,
      strokeMinusThree,
    } = this.props;

    const {
      xScale,
      chartConfig: { yScale, width },
      plotData,
      xAccessor,
      hovering,
    } = moreProps;

    if (!plotData || plotData.length === 0) return;

    // Calculate statistics directly from visible plotData
    const valuationData = this.calculateValuationLines(
      plotData,
      sourcePath || "close",
      averageType || "mean",
    );
    if (!valuationData) return;

    // Calculate x range for lines (full chart width)
    const firstX = xScale(xAccessor(plotData[0]));
    const lastX = xScale(xAccessor(plotData[plotData.length - 1]));

    // Position for price labels (right edge of chart)
    const labelX = width;

    // Draw each line if enabled with price labels
    if (displayAverage && isDefined(valuationData.average)) {
      const y = yScale(valuationData.average);
      this.drawHorizontalLine(ctx, firstX, lastX, y, strokeAverage, hovering);
      this.drawPriceLabel(
        ctx,
        labelX,
        y,
        valuationData.average,
        strokeAverage.color,
      );
    }

    if (displayOneStdDev) {
      if (isDefined(valuationData.plusOneStdDev)) {
        const y = yScale(valuationData.plusOneStdDev);
        this.drawHorizontalLine(ctx, firstX, lastX, y, strokePlusOne, hovering);
        this.drawPriceLabel(
          ctx,
          labelX,
          y,
          valuationData.plusOneStdDev,
          strokePlusOne.color,
        );
      }
      if (isDefined(valuationData.minusOneStdDev)) {
        const y = yScale(valuationData.minusOneStdDev);
        this.drawHorizontalLine(
          ctx,
          firstX,
          lastX,
          y,
          strokeMinusOne,
          hovering,
        );
        this.drawPriceLabel(
          ctx,
          labelX,
          y,
          valuationData.minusOneStdDev,
          strokeMinusOne.color,
        );
      }
    }

    if (displayTwoStdDev) {
      if (isDefined(valuationData.plusTwoStdDev)) {
        const y = yScale(valuationData.plusTwoStdDev);
        this.drawHorizontalLine(ctx, firstX, lastX, y, strokePlusTwo, hovering);
        this.drawPriceLabel(
          ctx,
          labelX,
          y,
          valuationData.plusTwoStdDev,
          strokePlusTwo.color,
        );
      }
      if (isDefined(valuationData.minusTwoStdDev)) {
        const y = yScale(valuationData.minusTwoStdDev);
        this.drawHorizontalLine(
          ctx,
          firstX,
          lastX,
          y,
          strokeMinusTwo,
          hovering,
        );
        this.drawPriceLabel(
          ctx,
          labelX,
          y,
          valuationData.minusTwoStdDev,
          strokeMinusTwo.color,
        );
      }
    }

    if (displayThreeStdDev) {
      if (isDefined(valuationData.plusThreeStdDev)) {
        const y = yScale(valuationData.plusThreeStdDev);
        this.drawHorizontalLine(
          ctx,
          firstX,
          lastX,
          y,
          strokePlusThree,
          hovering,
        );
        this.drawPriceLabel(
          ctx,
          labelX,
          y,
          valuationData.plusThreeStdDev,
          strokePlusThree.color,
        );
      }
      if (isDefined(valuationData.minusThreeStdDev)) {
        const y = yScale(valuationData.minusThreeStdDev);
        this.drawHorizontalLine(
          ctx,
          firstX,
          lastX,
          y,
          strokeMinusThree,
          hovering,
        );
        this.drawPriceLabel(
          ctx,
          labelX,
          y,
          valuationData.minusThreeStdDev,
          strokeMinusThree.color,
        );
      }
    }
  }

  drawHorizontalLine(ctx, x1, x2, y, stroke, hovering = false) {
    if (!stroke || !stroke.color) return;

    // Set up stroke style with hover highlight
    ctx.strokeStyle = hexToRGBA(stroke.color, stroke.opacity);
    ctx.lineWidth = hovering ? (stroke.width + 1) * 1.5 : stroke.width;
    const dashArray = getStrokeDasharray(stroke.dash);
    ctx.setLineDash(
      dashArray === "Solid" || !dashArray
        ? []
        : dashArray.split(",").map(Number),
    );

    ctx.beginPath();
    ctx.moveTo(x1, y);
    ctx.lineTo(x2, y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  drawPriceLabel(ctx, edgeX, y, value, fill) {
    if (!isDefined(value)) return;

    // Use global formatting function for locale-aware price formatting (Portuguese: 1.234,56)
    const text = formatNumber(value, 2);
    const padding = 5;
    const fontSize = 11;
    const rectHeight = 20;

    // Measure text width
    ctx.font = `${fontSize}px Helvetica Neue, Helvetica, Arial, sans-serif`;
    const textWidth = ctx.measureText(text).width;
    const rectWidth = textWidth + padding * 2;

    // Position rectangle at the right edge (orient: right)
    const rectX = edgeX + 1;
    const rectY = y - rectHeight / 2;

    // Draw background rectangle
    ctx.fillStyle = hexToRGBA(fill, 1);
    ctx.beginPath();
    ctx.rect(rectX, rectY, rectWidth, rectHeight);
    ctx.fill();

    // Draw text centered in rectangle
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, rectX + rectWidth / 2, y);
  }

  renderSVG(moreProps) {
    const { className, sourcePath, averageType } = this.props;
    const {
      displayAverage,
      displayOneStdDev,
      displayTwoStdDev,
      displayThreeStdDev,
      strokeAverage,
      strokePlusOne,
      strokeMinusOne,
      strokePlusTwo,
      strokeMinusTwo,
      strokePlusThree,
      strokeMinusThree,
    } = this.props;

    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    if (!plotData || plotData.length === 0) return null;

    // Calculate statistics directly from visible plotData
    const valuationData = this.calculateValuationLines(
      plotData,
      sourcePath || "close",
      averageType || "mean",
    );
    if (!valuationData) return null;

    // Calculate x range for lines (full chart width)
    const firstX = xScale(xAccessor(plotData[0]));
    const lastX = xScale(xAccessor(plotData[plotData.length - 1]));

    return (
      <g className={className}>
        {displayAverage &&
          isDefined(valuationData.average) &&
          this.renderHorizontalLine(
            firstX,
            lastX,
            yScale(valuationData.average),
            strokeAverage,
            "average",
          )}

        {displayOneStdDev &&
          isDefined(valuationData.plusOneStdDev) &&
          this.renderHorizontalLine(
            firstX,
            lastX,
            yScale(valuationData.plusOneStdDev),
            strokePlusOne,
            "plus1",
          )}
        {displayOneStdDev &&
          isDefined(valuationData.minusOneStdDev) &&
          this.renderHorizontalLine(
            firstX,
            lastX,
            yScale(valuationData.minusOneStdDev),
            strokeMinusOne,
            "minus1",
          )}

        {displayTwoStdDev &&
          isDefined(valuationData.plusTwoStdDev) &&
          this.renderHorizontalLine(
            firstX,
            lastX,
            yScale(valuationData.plusTwoStdDev),
            strokePlusTwo,
            "plus2",
          )}
        {displayTwoStdDev &&
          isDefined(valuationData.minusTwoStdDev) &&
          this.renderHorizontalLine(
            firstX,
            lastX,
            yScale(valuationData.minusTwoStdDev),
            strokeMinusTwo,
            "minus2",
          )}

        {displayThreeStdDev &&
          isDefined(valuationData.plusThreeStdDev) &&
          this.renderHorizontalLine(
            firstX,
            lastX,
            yScale(valuationData.plusThreeStdDev),
            strokePlusThree,
            "plus3",
          )}
        {displayThreeStdDev &&
          isDefined(valuationData.minusThreeStdDev) &&
          this.renderHorizontalLine(
            firstX,
            lastX,
            yScale(valuationData.minusThreeStdDev),
            strokeMinusThree,
            "minus3",
          )}
      </g>
    );
  }

  renderHorizontalLine(x1, x2, y, stroke, key) {
    if (!stroke || !stroke.color) return null;

    return (
      <line
        key={key}
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        stroke={stroke.color}
        strokeWidth={stroke.width}
        strokeOpacity={stroke.opacity}
        strokeDasharray={getStrokeDasharray(stroke.dash)}
      />
    );
  }

  isHover(moreProps) {
    const {
      highlightOnHover,
      hoverTolerance,
      onHover,
      onUnHover,
      onContextMenu,
      onDoubleClick,
      onClick,
      displayAverage,
      displayOneStdDev,
      displayTwoStdDev,
      displayThreeStdDev,
      sourcePath,
      averageType,
    } = this.props;

    const needsHover =
      highlightOnHover ||
      onHover ||
      onUnHover ||
      onContextMenu ||
      onDoubleClick ||
      onClick;
    if (!needsHover) return false;

    const tolerance = Number.isFinite(hoverTolerance) ? hoverTolerance : 6;
    const {
      mouseXY,
      chartConfig: { origin },
      plotData,
    } = moreProps;
    if (!mouseXY || !plotData || plotData.length === 0) return false;

    const [mouseXRaw, mouseYRaw] = mouseXY;
    const mouseY = mouseYRaw + origin[1];

    // Calculate valuation lines
    const valuationData = this.calculateValuationLines(
      plotData,
      sourcePath || "close",
      averageType || "mean",
    );
    if (!valuationData) return false;

    const {
      chartConfig: { yScale },
    } = moreProps;

    // Check if mouse is near any enabled line
    const linesToCheck = [];

    if (displayAverage && isDefined(valuationData.average)) {
      linesToCheck.push(yScale(valuationData.average));
    }
    if (displayOneStdDev) {
      if (isDefined(valuationData.plusOneStdDev))
        linesToCheck.push(yScale(valuationData.plusOneStdDev));
      if (isDefined(valuationData.minusOneStdDev))
        linesToCheck.push(yScale(valuationData.minusOneStdDev));
    }
    if (displayTwoStdDev) {
      if (isDefined(valuationData.plusTwoStdDev))
        linesToCheck.push(yScale(valuationData.plusTwoStdDev));
      if (isDefined(valuationData.minusTwoStdDev))
        linesToCheck.push(yScale(valuationData.minusTwoStdDev));
    }
    if (displayThreeStdDev) {
      if (isDefined(valuationData.plusThreeStdDev))
        linesToCheck.push(yScale(valuationData.plusThreeStdDev));
      if (isDefined(valuationData.minusThreeStdDev))
        linesToCheck.push(yScale(valuationData.minusThreeStdDev));
    }

    // Check if mouse Y is within tolerance of any line
    for (const lineY of linesToCheck) {
      if (Math.abs(mouseY - lineY) <= tolerance) {
        return true;
      }
    }

    return false;
  }

  render() {
    const {
      highlightOnHover,
      onHover,
      onUnHover,
      onClick,
      onDoubleClick,
      onContextMenu,
    } = this.props;

    const needsHover =
      highlightOnHover ||
      onHover ||
      onUnHover ||
      onContextMenu ||
      onDoubleClick ||
      onClick;
    const hoverProps = needsHover
      ? {
          isHover: this.isHover,
          drawOn: ["mousemove", "pan"],
          canvasToDraw: getMouseCanvas,
        }
      : {
          drawOn: ["pan"],
          canvasToDraw: getAxisCanvas,
        };

    return (
      <GenericChartComponent
        edgeClip
        clip={false}
        canvasDraw={this.drawOnCanvas}
        svgDraw={this.renderSVG}
        onHover={onHover}
        onUnHover={onUnHover}
        onClickWhenHover={onClick}
        onDoubleClickWhenHover={onDoubleClick}
        onContextMenuWhenHover={onContextMenu}
        {...hoverProps}
      />
    );
  }
}

ValuationLinesSeries.propTypes = {
  className: PropTypes.string,
  sourcePath: PropTypes.string,
  averageType: PropTypes.string,
  displayAverage: PropTypes.bool,
  displayOneStdDev: PropTypes.bool,
  displayTwoStdDev: PropTypes.bool,
  displayThreeStdDev: PropTypes.bool,
  strokeAverage: PropTypes.object,
  strokePlusOne: PropTypes.object,
  strokeMinusOne: PropTypes.object,
  strokePlusTwo: PropTypes.object,
  strokeMinusTwo: PropTypes.object,
  strokePlusThree: PropTypes.object,
  strokeMinusThree: PropTypes.object,
  highlightOnHover: PropTypes.bool,
  hoverTolerance: PropTypes.number,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
};

ValuationLinesSeries.defaultProps = {
  className: "react-stockcharts-valuation-lines-series",
  displayAverage: true,
  displayOneStdDev: false,
  displayTwoStdDev: false,
  displayThreeStdDev: false,
  // Colors from ChartIQ (studiesChartIQ.js lines 975-979)
  strokeAverage: { color: "#00afed", opacity: 1, width: 1, dash: "Solid" }, // Blue
  strokePlusOne: { color: "#e1e1e1", opacity: 1, width: 1, dash: "Solid" }, // Light gray
  strokeMinusOne: { color: "#e1e1e1", opacity: 1, width: 1, dash: "Solid" }, // Light gray
  strokePlusTwo: { color: "#85c99e", opacity: 1, width: 1, dash: "Solid" }, // Green
  strokeMinusTwo: { color: "#85c99e", opacity: 1, width: 1, dash: "Solid" }, // Green
  strokePlusThree: { color: "#fff69e", opacity: 1, width: 1, dash: "Solid" }, // Yellow
  strokeMinusThree: { color: "#fff69e", opacity: 1, width: 1, dash: "Solid" }, // Yellow
  highlightOnHover: true,
  hoverTolerance: 6,
};

export default ValuationLinesSeries;
